import { useCallback } from 'react';

import { getIsPdfLocationAnchor, parseAnchorId, Queries } from '@aline/common';
import type { NodeEntry } from 'slate';
import { Editor } from 'slate';
import { parseFileLocationId } from 'src/components/ai/chat/ChatPanelMarkdown/helpers/parseLocation';
import { useScrollToLocationForReference } from 'src/editor';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useMaybeDocumentParams } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';

/**
 * Scrolls to and selects the block a proposal card references — the paragraph
 * for in-paragraph edits and inserts, the table or row for table-structure
 * edits — so the reader can see where a suggestion would land before anything
 * is materialized. That is also why this cannot reuse the suggestion-unit
 * navigation the chat cards use: a proposal has no suggestions in the
 * document yet, only an anchor.
 */
export function usePlaybookNavigate() {
  const editor = useMaybeEditor();
  const { documentId: openDocumentId } = useMaybeDocumentParams();
  const { scrollToLocationForReference } = useScrollToLocationForReference();
  const windowHeight = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const scrollOffset = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  const navigateToCardAnchor = useCallback(
    (options: { documentId: string; locationAnchor: string }) => {
      const { documentId, locationAnchor } = options;
      // An anchor is only meaningful within its own document.
      if (documentId !== openDocumentId) return;

      /**
       * PDF documents render in the Apryse viewer, not a Slate editor, so
       * highlight the source text by its coordinate anchor — the same path
       * citations use.
       */
      if (getIsPdfLocationAnchor(locationAnchor)) {
        const aiReference = parseFileLocationId(locationAnchor, '');
        if (aiReference) scrollToLocationForReference({ aiReference });
        return;
      }

      if (!editor) return;
      const entry = getAnchorEntry(editor, locationAnchor);
      if (!entry) return;

      const range = {
        anchor: Editor.start(editor, entry[1]),
        focus: Editor.end(editor, entry[1]),
      };
      ClientTransforms.scrollToLocation(editor, {
        cMLocation: range,
        rWHeightPx: windowHeight,
        rWOffsetPx: scrollOffset,
        scrollAlignment: 'center',
      });
    },
    [editor, openDocumentId, scrollOffset, scrollToLocationForReference, windowHeight]
  );

  return { navigateToCardAnchor };
}

function getAnchorEntry(editor: Editor, locationAnchor: string): NodeEntry | undefined {
  const parsed = parseAnchorId(locationAnchor);
  switch (parsed.kind) {
    case 'table':
      return Queries.getTableByLocationId(editor, parsed.locationAnchor);
    case 'row':
      return Queries.getTableRowByLocationId(editor, parsed.locationAnchor);
    case 'paragraph':
      return Queries.getParagraphByLocationId(editor, parsed.locationAnchor);
  }
}
